import React, { useMemo, useCallback, memo } from "react";
import { Box, Stack, Typography, TextField, FormControlLabel, Checkbox, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AskAI from "../Images/AskAI.png";
import CancelIcon from "@mui/icons-material/Cancel";
import { FormAutocompleteRow, FormRow } from "../Components/FormAutocompleteRow";
import { CustomDatePicker } from "../HelperFunctions/EmployeeFormHelpers";
import Void_Stamp from "../Images/Void_Stamp.png";
import Reconciled from "../Images/Reconsiled.png";
import ReconciledResume from "../Images/Reconsiled_Resume.png"; // Adjust filename if needed
import Cleared from "../Images/cleared.png";
import dayjs from "dayjs";
import { usePayrollForm } from "../Context/PayrollFormContext";

// --- Constants ---
const RECON_STATUS = {
  RECONCILED: 6,
  RECONCILED_RESUME: 19,
};

// --- Subcomponents ---
const NetPayStamps = memo(({ isVoidStamp, reconStatus, clearedDate }) => {
  let currentLeft = 110;
  const stamps = [];

  if (isVoidStamp) {
    stamps.push(
      <img key="void" src={Void_Stamp} alt="Void" width={50} style={{ position: "absolute", top: -15, left: currentLeft, zIndex: 10, pointerEvents: "none" }} />
    );
    currentLeft += 80;
  }

  if (reconStatus === RECON_STATUS.RECONCILED) {
    stamps.push(
      <img key="recon" src={Reconciled} alt="Reconciled" width={50} style={{ position: "absolute", top: -15, left: currentLeft, zIndex: 10, pointerEvents: "none" }} />
    );
    currentLeft += 60;
  } else if (reconStatus === RECON_STATUS.RECONCILED_RESUME) {
    stamps.push(
      <img key="recon-resume" src={ReconciledResume} alt="Reconciled Resume" width={50} style={{ position: "absolute", top: -15, left: currentLeft, zIndex: 10, pointerEvents: "none" }} />
    );
    currentLeft += 60;
  }

  if (clearedDate) {
    stamps.push(
      <Box key="cleared" sx={{ position: "absolute", top: -15, left: currentLeft, zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <img src={Cleared} alt="Cleared" width={50} style={{ pointerEvents: "none" }} />
        <Typography sx={{ fontSize: "10px", fontWeight: 700, mt: "-4px" }}>
          {clearedDate}
        </Typography>
      </Box>
    );
  }

  return <>{stamps}</>;
});

const PayPeriodFormInfo = (
  // formValues,
  // handleChange,
  // handleNameChange,
  // displayName,
  // nameOptions,
  // departments,
  // jobTitles,
  // profitCenters,
  // row,
  // values,
  // isVoid,
  // setIsVoid,
  // voidDate,
  // setVoidDate,
  // comments,
  // setComments,
  // voidDatePref,
  // handleVoidClick,
  // showErrorSnackbar,
  // employeeTotalGross,
  // payPeriodTotals,
  // isReadOnly,
  // onAskAiClick,
  // showAskAiButton,
  /*props*/) => {
  
const {
  formValues,
  handleChange,
  handleNameChange,
  displayName,
  nameOptions,
  departments,
  jobTitles,
  profitCenters,
  row,
  values,
  isVoid,
  setIsVoid,
  voidDate,
  setVoidDate,
  comments,
  setComments,
  voidDatePref,
  handleVoidClick,
  showErrorSnackbar,
  employeeTotalGross,
  payPeriodTotals,
  isReadOnly,
  onAskAiClick,
  showAskAiButton,
} = usePayrollForm();

  // --- Computations ---
  const netPay = useMemo(() => {
    return (
      (employeeTotalGross || 0) +
      (payPeriodTotals?.totalAdditions || 0) -
      (payPeriodTotals?.totalDeductions || 0) -
      (payPeriodTotals?.totalEETaxes || 0)
    );
  }, [employeeTotalGross, payPeriodTotals]);

  // --- Handlers ---
  const handleVoidDateChange = useCallback((name, val) => {
    const newDate = val ? dayjs(val) : null;
    const payDate = values.payDate ? dayjs(values.payDate) : null;
    
    if (newDate && payDate && newDate.isBefore(payDate, "day")) {
      if (showErrorSnackbar) {
        showErrorSnackbar("Void Date cannot be less than Pay Date.");
      }
      setVoidDate(payDate);
    } else {
      setVoidDate(newDate);
    }
  }, [values.payDate, setVoidDate, showErrorSnackbar]);

  const commonFieldBox = {
    flex: 1,
    minWidth: 275,
  };

  const importedEmpName = formValues?.importedName ?? values?.importedName;
  const isImportedNameEmpty = !importedEmpName || String(importedEmpName).trim() === "" || String(importedEmpName).toLowerCase() === "null" || String(importedEmpName).toLowerCase() === "undefined";
  const isType1 = isImportedNameEmpty || values?.importPayrollType === 1 || values?.isAddPayRoll;

  return (
    <>
      {/* Row 1 */}
      <Stack direction="row" spacing={"9%"} alignItems="flex-start">
        <Box sx={commonFieldBox} >
          <FormAutocompleteRow
            label="Name"
            value={formValues.name || ""}
            displayValue={displayName}
            importedValue={!isType1 ? formValues.importedName : null}
            options={nameOptions}
            onChange={handleNameChange}
            helperText={!isType1 && values.importPayrollType === 2 && "Name to be mapped as per the system"}
            disabled={isType1 || values.importPayrollType !== 2}
          />
        </Box>

        <Box sx={commonFieldBox}>
          <FormAutocompleteRow
            label="Department"
            value={formValues.department || ""}
            importedValue={!isType1 ? formValues.importedDept : null}
            options={[{ label: "--Select--", value: "" }, ...(departments || [])]}
            onChange={handleChange("department")}
            disabled={isReadOnly}
          />
        </Box>

        <Box sx={commonFieldBox}>
          <Stack spacing={0.5}>
            <FormAutocompleteRow
              label="Job"
              value={formValues.job || ""}
              importedValue={!isType1 ? formValues.importedJob : null}
              options={[{ label: "--Select--", value: "" }, ...(jobTitles || [])]}
              onChange={handleChange("job")}
              disabled={isReadOnly}
            />
            {showAskAiButton && (
              <Button
                size="small"
                startIcon={<img src={AskAI} alt="Ask AI" style={{ width: 14, height: 14 }} />}
                onClick={onAskAiClick}
                sx={{ textTransform: 'none', fontSize: '11px', alignSelf: 'flex-end', p: '2px 8px' }}
              >
                ASK AI
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>

      {/* Row 2 */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: "9%" }} alignItems="flex-start">
        {profitCenters && profitCenters.length > 0 && (
          <Box sx={commonFieldBox}>
            <FormAutocompleteRow
              label="Profit Center"
              value={formValues.profitCenter || ""}
              options={[{ label: "--Select--", value: "" }, ...profitCenters]}
              onChange={handleChange("profitCenter")}
              disabled={isReadOnly}
            />
          </Box>
        )}

        <Box sx={commonFieldBox}>
          <FormRow label="Cheque No">
            <TextField
              size="small"
              value={formValues.checkNo}
              onChange={handleChange("checkNo")}
              disabled={isReadOnly}
              sx={{
                bgcolor: "white", width: "100%", minWidth: "90%",
                "& .MuiOutlinedInput-root.Mui-disabled": { backgroundColor: "#f5f5f5 !important" },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000 !important", color: "#000 !important",
                }
              }}
            />
          </FormRow>
        </Box>

        <Box sx={commonFieldBox}>
          <FormRow label="NET PAY" importedValue={!isType1 && values?.importPayrollType === 2 ? formValues.importedNetPay : null}>
            <Box sx={{ position: "relative" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography fontSize={"0.75rem"} fontWeight="bold">
                  <b>{netPay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
                </Typography>
                {(() => {
                  const isImportType2 = !isType1 && values?.importPayrollType === 2;
                  const importedNetPayNum = parseFloat(String(formValues.importedNetPay || "0").replace(/,/g, ""));
                  const isMatched = isImportType2 ? (Math.abs(netPay - importedNetPayNum) < 0.01) : (row?.statusText === "Matched" || row?.view === "Matched");
                  
                  return isMatched ? (
                    <CheckCircleIcon fontSize="medium" color="success" />
                  ) : (
                    <CancelIcon fontSize="medium" color="error" />
                  );
                })()}
              </Stack>
              <NetPayStamps 
                isVoidStamp={formValues.isVoidStamp} 
                reconStatus={formValues.reconStatus} 
                clearedDate={formValues.clearedDate} 
              />
            </Box>
          </FormRow>
        </Box>

        {/* Empty box to maintain 3-column alignment when Profit Center is hidden */}
        {!(profitCenters && profitCenters.length > 0) && (
          <Box sx={commonFieldBox} />
        )}
      </Stack>

      {/* Void Section */}
      {(row?.statusText === "Matched" || row?.view === "Matched") && (!formValues.isVoidStamp || voidDatePref) && !formValues?.reconStatus && !formValues?.clearedDate && (
        <Stack direction="row" alignItems="center" spacing={3} sx={{ mt: 1 }}>
          {voidDatePref ? (
            <FormControlLabel
              control={<Checkbox size="small" disabled={isReadOnly} checked={isVoid} onChange={(e) => setIsVoid(e.target.checked)} sx={{ p: 0.5 }} />}
              label={<Typography fontSize={12} fontWeight={500}>Void</Typography>}
            />
          ) : (
            <Button
              variant="contained"
              size="small"
              color="primary"
                disabled={isVoid || formValues.isVoidStamp || isReadOnly}
              onClick={handleVoidClick}
              sx={{ fontSize: 11, height: 24, minWidth: 80, textTransform: "none" }}
            >
              Void
            </Button>
          )}

          {voidDatePref && isVoid && (
            <Stack direction="row" spacing={"16%"} sx={{ flex: 1 }} alignItems="center">
              <Box sx={{}}>
                <FormRow label="Void Date" width={"180px"}>
                  <CustomDatePicker
                    name="voidDate"
                    value={voidDate}
                    setFieldValue={handleVoidDateChange}
                    heading=""
                    disabled={isReadOnly}
                    labelWidth={0}
                  />
                </FormRow>
              </Box>
              <Box sx={{ flex: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography fontSize={12}>Reason:</Typography>
                  <TextField
                    size="small"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                      disabled={isReadOnly}
                    multiline
                    minRows={2}
                    maxRows={2}
                    sx={{
                      width: 300,
                        "& .MuiOutlinedInput-root.Mui-disabled": { backgroundColor: "#f5f5f5 !important" },
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "#000 !important", color: "#000 !important",
                        },
                      "& .MuiOutlinedInput-root": {
                        fontSize: "11px",
                        padding: "6px 8px",
                      },
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
          )}
        </Stack>
      )}
    </>
  );
};

export default PayPeriodFormInfo;
